import React from 'react';
import { graphql, Link } from 'gatsby';
import { get } from 'lodash';
import { getImage } from 'gatsby-plugin-image';
import { Social } from '@/components/Social';
import { Button } from '@/components/Button';
import { TestimonialsTable } from '@/components/TestimonialsTable';
import Layout, { Main } from '../../components/Layout';
import { Content } from '../../components/Content';
import { Text } from '../../components/Text';
import { Seo } from '../../components/SEO';
import { MdContent } from '../../components/MdContent';
import { CallToAction } from '../../components/CallToAction';
import { StyledProjects } from './styles';

interface ITestimonialsPageTemplate {
  // eslint-disable-next-line react/no-unused-prop-types
  content?: any;
  testimonials: any;
}

interface ITestimonialsPage {
  data: {
    site: {
      siteMetadata: {
        title: string;
        siteUrl: string;
      };
    };
    contentfulPage: any;
    allContentfulTestimonials: any;
  };
  location: object;
}

const TestimonialsPageTemplate = ({
  content,
  testimonials,
}: ITestimonialsPageTemplate) => {
  const { title, subtitle, body } = content;
  const { body: mdBody } = body;

  return (
    <>
      <Content
        restrictWidth
        setMaxWidth="75%"
        className="mobile-content-welcome"
      >
        <Text type="title1" tag="h2" color="white_1" className="main-title">
          {title}
        </Text>
        <Text type="subtitle1" tag="h3" color="white_1_45">
          {subtitle}
        </Text>
        <Text type="body1" tag="div" color="gray_3">
          <MdContent content={mdBody} />
        </Text>
      </Content>

      <Content
        restrictWidth
        setMaxWidth="90%"
        disableTextStyles
        enableFlex
        className="last"
      >
        <TestimonialsTable
          data={testimonials.edges.map(({ node }) => node)}
          slider={false}
        />
      </Content>

      <Content restrictWidth shouldCenter>
        <Link to="/#testimonials">
          <Button size="large" disabled={false}>
            Back to Home
          </Button>
        </Link>
      </Content>
    </>
  );
};

const TestimonialsPageDesktopTemplate = ({
  testimonials,
}: ITestimonialsPageTemplate) => {
  return (
    <div className="body">
      <Content
        restrictWidth
        setMaxWidth="100%"
        disableTextStyles
        enableFlex
        noMargin
      >
        <TestimonialsTable
          data={testimonials.edges.map(({ node }) => node)}
          slider={false}
        />
      </Content>

      <Content restrictWidth shouldCenter>
        <Link to="/#testimonials">
          <Button size="large" disabled={false}>
            Back to Home
          </Button>
        </Link>
      </Content>
    </div>
  );
};

const TestimonialsPage = ({ data, location }: ITestimonialsPage) => {
  const allTestimonials = get(data, `allContentfulTestimonial`);
  const page = get(data, `contentfulPage`);
  const {
    title,
    subtitle,
    body,
    slug,
    metaDescription,
    metaTitle,
    templateKey,
    heroImage,
    svgTopPath,
    svgBottomPath,
    ctaTitle,
    ctaHookId,
    ctaIcon: pageCTAIcon,
    ctaBody,
  } = page;

  const hero = getImage(heroImage);
  const ctaIcon = getImage(pageCTAIcon);
  return (
    <StyledProjects>
      <Layout
        location={location}
        coverImage={hero}
        title={title}
        topSvg={svgTopPath.svg.content}
        bottomSvg={svgBottomPath.svg.content}
        desktopHeading={
          <Content restrictWidth>
            <Text type="title1" tag="h2" color="white_1">
              {title}
            </Text>
            <Text type="subtitle1" tag="h3" color="white_1_45">
              {subtitle}
            </Text>
            <Text type="body1" tag="div" color="gray_3">
              <MdContent content={body.body} />
            </Text>
          </Content>
        }
        desktopBody={
          <TestimonialsPageDesktopTemplate testimonials={allTestimonials} />
        }
        desktopCta={
          <CallToAction
            hookId={ctaHookId}
            icon={ctaIcon}
            title={ctaTitle}
            body={ctaBody.ctaBody}
            maxWidth="100%"
          />
        }
      >
        <Seo
          title={title}
          metaTitle={metaTitle}
          metaDesc={metaDescription}
          slug={slug}
          templateKey={templateKey}
        />
        <Main background="transparent" disableVertPadding={false}>
          <div className="mobile-main">
            <TestimonialsPageTemplate
              content={{
                title,
                subtitle,
                body,
                ctaTitle,
                ctaHookId,
                ctaIcon,
                ctaBody,
              }}
              testimonials={allTestimonials}
            />
            <CallToAction
              hookId={ctaHookId}
              icon={ctaIcon}
              title={ctaTitle}
              body={ctaBody.ctaBody}
            />
            <Social />
          </div>
        </Main>
      </Layout>
    </StyledProjects>
  );
};

export default TestimonialsPage;

export const TestimonialsPageQuery = graphql`
  query TestimonialsPage($slug: String!) {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    contentfulPage(slug: { eq: $slug }) {
      templateKey
      title
      subtitle
      slug
      body {
        body
      }
      heroImage {
        gatsbyImageData(
          width: 560
          height: 900
          quality: 85
          placeholder: BLURRED
        )
      }
      svgTopPath {
        svg {
          content
        }
      }
      svgBottomPath {
        svg {
          content
        }
      }
      metaTitle
      metaDescription
      ctaTitle
      ctaHookId
      ctaIcon {
        gatsbyImageData(width: 48, quality: 85, placeholder: BLURRED)
      }
      ctaBody {
        ctaBody
      }
    }
    allContentfulTestimonial {
      edges {
        node {
          linkedInUrl
          name
          project {
            name
            slug
            templateKey
          }
          image {
            gatsbyImageData(
              width: 226
              height: 226
              quality: 100
              placeholder: BLURRED
            )
          }
          id
          role
          recommendation {
            recommendation
          }
        }
      }
    }
  }
`;
